import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AccountRow from './AccountRow';
import { switchAccount, removeAccount, userLogout } from '../../store/user';
import { ACCOUNT_CAP_MESSAGE } from '../../constants/accounts';
import { useAccountCap } from '../../hooks/useAccountCap';

/** Account-aware panel shown on /login and /signup while signed in (#164/#168).
 * Identifies the signed-in account(s) and offers: go to the app, switch account,
 * sign out of the current account. The page renders the add-account form below
 * it unless the account cap is reached (see useAccountCap). */

function SignedInAccountsPanel() {
	const dispatch = useDispatch();
	const history = useHistory();
	const activeUsername = useSelector((st) => st.user.activeUsername);
	const currentUser = useSelector((st) => st.user.currentUser);
	const { accounts, atCap } = useAccountCap();

	// Legacy/edge: a projection without an account list still shows who is signed in.
	const rows = accounts.length
		? accounts
		: [{ username: currentUser?.username, currentUser }];
	const active = activeUsername || currentUser?.username;

	const handleSwitch = (username) => {
		if (username !== active) dispatch(switchAccount(username));
		history.push('/home');
	};

	const handleSignOutActive = () => {
		// No account list (legacy projection only): a full reset is the only sign-out.
		if (!activeUsername) {
			dispatch(userLogout());
			history.push('/login');
			return;
		}
		const remaining = accounts.filter((a) => a.username !== activeUsername);
		dispatch(removeAccount(activeUsername));
		if (remaining.length === 0) history.push('/login');
	};

	return (
		<section
			className='mt-[25px] rounded-md border border-gray-200 p-4'
			aria-label='Signed-in accounts'
			data-testid='signed-in-panel'>
			<h2 className='font-glacial m-0 text-[20px] font-bold text-clientNero'>
				You're signed in
			</h2>
			<ul className='mt-3 space-y-1 p-0'>
				{rows.map((account) => (
					<li key={account.username} className='list-none'>
						<button
							type='button'
							onClick={() => handleSwitch(account.username)}
							className='w-full rounded-md px-2 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary'
							aria-current={account.username === active ? 'true' : undefined}>
							<AccountRow account={account} active={account.username === active} />
						</button>
					</li>
				))}
			</ul>
			<div className='mt-3 flex flex-wrap gap-2'>
				<button
					type='button'
					onClick={() => history.push('/home')}
					className='rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-primary'>
					Go to app
				</button>
				<button
					type='button'
					onClick={handleSignOutActive}
					className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary'>
					Sign out of {active}
				</button>
			</div>
			{atCap && (
				<p className='mt-3 mb-0 text-sm text-gray-500' data-testid='account-cap-note'>
					{ACCOUNT_CAP_MESSAGE}
				</p>
			)}
		</section>
	);
}

export default SignedInAccountsPanel;
